// Standardized ScrollTrigger config for Voyantis animations.
//
// Default trigger position: element's top is 75% from the top of the
// viewport (i.e. ~25% of the viewport down from the bottom). This fires
// once a section has clearly entered view — late enough that the user
// notices the animation, early enough that it doesn't feel delayed.
//
// (The original Figma annotation called for "top bottom-=100" but that
// triggered while sections were barely peeking in, making the animation
// effectively invisible for tall layouts.)
//
// Requires: gsap, ScrollTrigger (loaded via Webflow custom code).

/** Default trigger start position used by every scroll-reveal preset. */
export const DEFAULT_START = 'top 75%';

/**
 * Returns a ScrollTrigger config object with project defaults filled in.
 * Pass overrides (trigger, start, once, etc.) and they win.
 *
 *   scrollTriggerConfig({ trigger: section })
 *   scrollTriggerConfig({ trigger: section, start: 'top 60%' }) // override start
 *   scrollTriggerConfig({ trigger: section, once: false })       // re-trigger
 */
export function scrollTriggerConfig({
  trigger,
  start = DEFAULT_START,
  end,
  once = true,
  scrub,
  pin,
  pinSpacing,
  markers,
  onUpdate,
  onEnter,
} = {}) {
  return {
    trigger,
    start,
    ...(end !== undefined && { end }),
    // once: true → toggleActions "play none none none" (no reverse, no replay)
    // once: false → "play none none reverse" (reverses on scroll-out)
    toggleActions: once ? 'play none none none' : 'play none none reverse',
    ...(scrub !== undefined && { scrub }),
    ...(pin !== undefined && { pin }),
    ...(pinSpacing !== undefined && { pinSpacing }),
    ...(markers !== undefined && { markers }),
    ...(onUpdate && { onUpdate }),
    ...(onEnter && { onEnter }),
  };
}

/**
 * Returns true if the user has requested reduced motion. Presets check this
 * and jump elements to their final state instead of animating.
 */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
